import { useCallback, useEffect, useRef, useState } from 'react';
import { assistedHeardInOrder } from '@orborix/shared-types';
import { openReviewLive, type ReviewLive } from '../../../lib/reviewLive.js';
import { createVoiceCounter, type VoiceCounter } from './assistedModel.js';

/** Lo que se sabe de la toma en curso: lo oído y las sílabas del cursor de voz. */
export interface ReviewLiveState {
  text: string;
  /**
   * Cuántas palabras visibles de la frase, desde el principio, ya se oyeron en
   * esta toma (`assistedHeardInOrder`). Es el máximo de la toma: no retrocede
   * cuando Scribe corrige un parcial (que no parpadee); vuelve a 0 con `begin`.
   */
  heard: number;
  syllables: number;
}

const EMPTY: ReviewLiveState = { text: '', heard: 0, syllables: 0 };

/**
 * Las palabras en vivo de una toma del repaso (Emmanuel, 15/10: «que te vaya
 * resaltando como en karaoke las palabras que vas diciendo»). Una escucha por
 * toma (`openReviewLive`): `begin(frase)` al abrir el micrófono, `feed` con cada
 * pedazo de audio, `end` al cerrar la toma. Lo oído se enciende siempre en orden
 * contra la frase (Emmanuel, 16/10: la escucha le erraba a la primera palabra y
 * quedaba un hueco al principio). Además del texto oído, cuenta la voz con el
 * mismo cursor que la charla asistida (`createVoiceCounter`): el primer parcial
 * del STT tarda ~1,6 s y el cursor da algo que ver mientras tanto.
 */
export function useReviewLive(): ReviewLiveState & {
  begin: (phrase: string) => void;
  feed: (samples: Float32Array, sampleRate: number) => void;
  end: () => void;
} {
  const [state, setState] = useState<ReviewLiveState>(EMPTY);
  const liveRef = useRef<ReviewLive | null>(null);
  const counterRef = useRef<VoiceCounter | null>(null);

  const begin = useCallback((phrase: string) => {
    liveRef.current?.close();
    counterRef.current = createVoiceCounter();
    setState(EMPTY);
    const live = openReviewLive({
      onText: (text, final) => {
        if (liveRef.current !== live) return; // de una toma anterior
        const now = assistedHeardInOrder(phrase, text);
        setState((s) => ({ ...s, text, heard: Math.max(s.heard, now) }));
        if (final) {
          live.close();
          liveRef.current = null;
        }
      },
    });
    liveRef.current = live;
  }, []);

  const feed = useCallback((samples: Float32Array, sampleRate: number) => {
    liveRef.current?.push(samples, sampleRate);
    const counter = counterRef.current;
    if (!counter) return;
    // Ventanas de 50 ms, como el cursor de la charla asistida.
    const win = Math.max(1, Math.round(sampleRate * 0.05));
    for (let at = 0; at < samples.length; at += win) {
      const end = Math.min(samples.length, at + win);
      let sum = 0;
      for (let i = at; i < end; i++) sum += samples[i]! * samples[i]!;
      counter.window(Math.sqrt(sum / (end - at)), ((end - at) / sampleRate) * 1000);
    }
    const syllables = counter.syllables();
    setState((s) => (s.syllables === syllables ? s : { ...s, syllables }));
  }, []);

  const end = useCallback(() => {
    counterRef.current = null;
    liveRef.current?.end();
  }, []);

  useEffect(
    () => () => {
      liveRef.current?.close();
      liveRef.current = null;
    },
    [],
  );

  return { ...state, begin, feed, end };
}
